import React, { useEffect } from 'react';
import { graphql, useFragment, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import type { MappedRule, RuleJqlInput } from '../../common/types.tsx';
import { formattingColors } from '../../common/utils/formatting-colors/index.tsx';
import type { FormattingRuleOperators } from '../../common/utils/operators/index.tsx';
import { useFormatRulesStore } from '../../controllers/format-rules-store/index.tsx';
import type { FormatRules$key } from './__generated__/FormatRules.graphql';
import type { FormatRulesQuery } from './__generated__/FormatRulesQuery.graphql';

/**
 * @deprecated Any changes made to this file should also be made to @atlassian/jira-issue-navigator-conditional-formatting-sidebar
 */
export const FormatRulesFragment = ({ project }: { project: FormatRules$key | null }) => {
	const [, { setFormatRules, setRulesForJqlInput }] = useFormatRulesStore();
	const projectData = useFragment<FormatRules$key>(
		graphql`
			fragment FormatRules on JiraProject @argumentDefinitions(first: { type: "Int!" }) {
				conditionalFormattingRules(first: $first) {
					edges {
						node {
							id
							formattingColor {
								colorKey
							}
							formattingArea
							expression {
								__typename
								... on JiraFormattingSingleValueOperand {
									fieldId
									singleValueOperator: operator
									value
								}
								... on JiraFormattingMultipleValueOperand {
									fieldId
									multipleValueOperator: operator
									values
								}
								... on JiraFormattingNoValueOperand {
									fieldId
									noValueOperator: operator
								}
							}
						}
					}
				}
			}
		`,
		project,
	);

	useEffect(() => {
		const formattingRulesByProject = projectData?.conditionalFormattingRules;

		if (!formattingRulesByProject) {
			return;
		}

		/* rowRules and cellRulesByFieldId - data to be able to format per rows and per field cell */
		let rowRules: MappedRule[] = [];
		const cellRulesByFieldId: { [fieldId: string]: MappedRule[] } = {};
		const rulesJqlInput: RuleJqlInput = {};

		formattingRulesByProject.edges?.forEach((edge, index) => {
			const rule = edge?.node;
			if (rule) {
				const { formattingArea, formattingColor } = rule;
				let fieldId = null;
				let operator: FormattingRuleOperators = 'IS';
				let value;
				switch (rule.expression.__typename) {
					case 'JiraFormattingSingleValueOperand':
						operator = rule.expression.singleValueOperator;
						({ value, fieldId } = rule.expression);
						break;
					case 'JiraFormattingMultipleValueOperand':
						operator = rule.expression.multipleValueOperator;
						value = rule.expression.values;
						fieldId = rule.expression.fieldId;
						break;
					case 'JiraFormattingNoValueOperand':
						operator = rule.expression.noValueOperator;
						fieldId = rule.expression.fieldId;
						break;
					default:
						break;
				}

				const color = formattingColors[formattingColor.colorKey || ''];
				const mappedRule = {
					id: rule.id,
					fieldId,
					operator,
					value,
					color: color?.value,
					formattingArea,
					index,
					hoverColor: color?.hoverValue,
				};

				if (mappedRule.fieldId) {
					if (mappedRule.formattingArea === 'ROW') {
						rowRules = [...rowRules, mappedRule];
					} else {
						cellRulesByFieldId[mappedRule.fieldId] = [
							...(cellRulesByFieldId[mappedRule.fieldId] || []),
							mappedRule,
						];
					}

					// input to serialise jql input
					const DUMMY_VALUE = '-';
					const values = Array.isArray(mappedRule.value) ? mappedRule.value : [mappedRule.value];
					if (!rulesJqlInput[mappedRule.fieldId]) rulesJqlInput[mappedRule.fieldId] = [];
					values.forEach((ruleValue) => {
						if (mappedRule.fieldId)
							rulesJqlInput[mappedRule.fieldId]?.push({
								value: ruleValue ?? DUMMY_VALUE,
								label: ruleValue ?? DUMMY_VALUE,
							});
					});
				}
			}
		});

		setFormatRules({
			rowRules,
			cellRulesByFieldId,
		});
		setRulesForJqlInput(rulesJqlInput);
	}, [projectData?.conditionalFormattingRules, setFormatRules, setRulesForJqlInput]);

	return null;
};

/**
 * @deprecated Any changes made to this file should also be made to @atlassian/jira-issue-navigator-conditional-formatting-sidebar
 */
export const FormatRules = ({
	query,
}: {
	query: PreloadedQuery<FormatRulesQuery>;
}): React.JSX.Element | null => {
	const [, { setFormatRules, setRulesForJqlInput }] = useFormatRulesStore();
	const { jira } = usePreloadedQuery<FormatRulesQuery>(
		graphql`
			query FormatRulesQuery($projectKey: String!, $first: Int!, $cloudId: ID!) @preloadable {
				jira {
					jiraProjectByKey(key: $projectKey, cloudId: $cloudId) {
						...FormatRules @arguments(first: $first)
						conditionalFormattingRules(first: $first) {
							edges {
								node {
									id
									formattingColor {
										colorKey
									}
									formattingArea
									expression {
										__typename
										... on JiraFormattingSingleValueOperand {
											fieldId
											singleValueOperator: operator
											value
										}
										... on JiraFormattingMultipleValueOperand {
											fieldId
											multipleValueOperator: operator
											values
										}
										... on JiraFormattingNoValueOperand {
											fieldId
											noValueOperator: operator
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		query,
	);

	if (fg('jira_list_conditional_formatting')) {
		return <FormatRulesFragment project={jira?.jiraProjectByKey ?? null} />;
	}

	// eslint-disable-next-line react-hooks/react-compiler
	// eslint-disable-next-line react-hooks/rules-of-hooks
	useEffect(() => {
		const formattingRulesByProject = jira?.jiraProjectByKey?.conditionalFormattingRules;

		if (!formattingRulesByProject) {
			return;
		}

		/* rowRules and cellRulesByFieldId - data to be able to format per rows and per field cell */
		let rowRules: MappedRule[] = [];
		const cellRulesByFieldId: { [fieldId: string]: MappedRule[] } = {};
		const rulesJqlInput: RuleJqlInput = {};

		formattingRulesByProject.edges?.forEach((edge, index) => {
			const rule = edge?.node;
			if (rule) {
				const { formattingArea, formattingColor } = rule;
				let fieldId = null;
				let operator: FormattingRuleOperators = 'IS';
				let value;
				switch (rule.expression.__typename) {
					case 'JiraFormattingSingleValueOperand':
						operator = rule.expression.singleValueOperator;
						({ value, fieldId } = rule.expression);
						break;
					case 'JiraFormattingMultipleValueOperand':
						operator = rule.expression.multipleValueOperator;
						value = rule.expression.values;
						fieldId = rule.expression.fieldId;
						break;
					case 'JiraFormattingNoValueOperand':
						operator = rule.expression.noValueOperator;
						fieldId = rule.expression.fieldId;
						break;
					default:
						break;
				}

				const color = formattingColors[formattingColor.colorKey || ''];
				const mappedRule = {
					id: rule.id,
					fieldId,
					operator,
					value,
					color: color?.value,
					formattingArea,
					index,
					hoverColor: color?.hoverValue,
				};

				if (mappedRule.fieldId) {
					if (mappedRule.formattingArea === 'ROW') {
						rowRules = [...rowRules, mappedRule];
					} else {
						cellRulesByFieldId[mappedRule.fieldId] = [
							...(cellRulesByFieldId[mappedRule.fieldId] || []),
							mappedRule,
						];
					}

					// input to serialise jql input
					const DUMMY_VALUE = '-';
					const values = Array.isArray(mappedRule.value) ? mappedRule.value : [mappedRule.value];
					if (!rulesJqlInput[mappedRule.fieldId]) rulesJqlInput[mappedRule.fieldId] = [];
					values.forEach((ruleValue) => {
						if (mappedRule.fieldId)
							rulesJqlInput[mappedRule.fieldId]?.push({
								value: ruleValue ?? DUMMY_VALUE,
								label: ruleValue ?? DUMMY_VALUE,
							});
					});
				}
			}
		});

		setFormatRules({
			rowRules,
			cellRulesByFieldId,
		});
		setRulesForJqlInput(rulesJqlInput);
	}, [jira?.jiraProjectByKey?.conditionalFormattingRules, setFormatRules, setRulesForJqlInput]);

	return null;
};
